import { useCallback, useRef, useState } from 'react';
import { uploadReleaseNoteImage } from '../services/releaseNotes.service';
import { describeApiError } from './errorMessage';

/** Mirrors the backend's multer limit in ReleaseNoteController. */
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

interface Options {
  value: string;
  onChange: (next: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

/**
 * Paste/drop image upload for the release-note markdown body.
 *
 * Follows the same approach as HelpDesk's useInlineImageUpload: a placeholder holds
 * the spot while the upload is in flight and is swapped by string match rather than
 * by caret offset — with two images pasted in quick succession, every offset after
 * the first insertion is stale, so matching on the placeholder keeps each upload
 * anchored to its own position regardless of completion order.
 */
export function useReleaseNoteImageUpload({ value, onChange, textareaRef }: Options) {
  const [uploadingCount, setUploadingCount] = useState(0);
  const [error, setError] = useState('');

  // `value` is captured per render, but the callbacks below resume after an await —
  // reading the prop there would splice into a stale snapshot and drop anything typed
  // during the upload. A ref refreshed every render gives them the live text.
  const valueRef = useRef(value);
  valueRef.current = value;

  const uploadSeq = useRef(0);

  const upload = useCallback(
    async (file: File) => {
      if (file.size > MAX_IMAGE_BYTES) {
        setError(`"${file.name || 'Image'}" is too large. Maximum size is 10MB.`);
        return;
      }

      setError('');
      const seq = ++uploadSeq.current;
      const displayName = file.name || 'pasted image';
      const placeholder = `![Uploading ${displayName}… #${seq}]()`;

      const textarea = textareaRef.current;
      const current = valueRef.current;
      const caret = textarea ? textarea.selectionStart : current.length;
      const before = current.slice(0, caret);
      const after = current.slice(caret);
      // Keep the image on its own line — an image mid-sentence renders awkwardly.
      const prefix = before && !before.endsWith('\n') ? '\n' : '';
      const suffix = after && !after.startsWith('\n') ? '\n' : '';
      const insertion = `${prefix}${placeholder}${suffix}`;
      onChange(`${before}${insertion}${after}`);

      const caretAfter = caret + insertion.length;
      requestAnimationFrame(() => {
        if (!textareaRef.current) return;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(caretAfter, caretAfter);
      });

      setUploadingCount((c) => c + 1);
      try {
        const res = await uploadReleaseNoteImage(file);
        if (res?.success && res.data?.url) {
          const alt = (res.data.name || displayName).replace(/[[\]]/g, '');
          onChange(valueRef.current.replace(placeholder, `![${alt}](${res.data.url})`));
        } else {
          onChange(valueRef.current.replace(placeholder, ''));
          setError('Image upload failed. Please try again.');
        }
      } catch (err: any) {
        onChange(valueRef.current.replace(placeholder, ''));
        setError(describeApiError(err, 'Image upload failed. Please try again.'));
      } finally {
        setUploadingCount((c) => c - 1);
      }
    },
    [onChange, textareaRef],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLElement>) => {
      // Browsers disagree about where a pasted image lands: some populate
      // clipboardData.files, others only clipboardData.items. Reading one path alone
      // silently breaks pasting in a subset of browsers, so check both and de-duplicate
      // (a browser populating both would otherwise upload the same image twice).
      const fromFiles = Array.from(e.clipboardData?.files || []);
      const fromItems = Array.from(e.clipboardData?.items || [])
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((f): f is File => !!f);

      const seen = new Set<string>();
      // Only images are intercepted — copying a screenshot alongside text (some tools
      // put both on the clipboard) must still paste the text normally.
      const images = [...fromFiles, ...fromItems].filter((f) => {
        if (!f.type.startsWith('image/')) return false;
        const key = `${f.name}:${f.size}:${f.lastModified}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      if (images.length === 0) return;
      e.preventDefault();
      images.forEach(upload);
    },
    [upload],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      const images = Array.from(e.dataTransfer?.files || []).filter((f) =>
        f.type.startsWith('image/'),
      );
      if (images.length === 0) return;
      e.preventDefault();
      images.forEach(upload);
    },
    [upload],
  );

  return {
    isUploading: uploadingCount > 0,
    error,
    clearError: useCallback(() => setError(''), []),
    handlePaste,
    handleDrop,
  };
}
